import React from "react";
import { Link, useParams } from "react-router-dom";

export function ConversationDetailPage() {
  const { id } = useParams<{ id: string }>();

  // Mock conversation data
  const conversation = {
    id: id || "1",
    name: "Q1 Product Launch",
    description:
      "Comprehensive conversation for our new product launch targeting enterprise customers",
    status: "active",
    createdAt: "2024-01-15",
    components: [
      {
        id: "client-1",
        type: "client",
        name: "Acme Corp",
        description: "Technology company",
        color: "indigo",
      },
      {
        id: "link-1",
        type: "link",
        name: "Product Demo Link",
        description: "activqms.com/demo-xyz",
        color: "blue",
        stats: { clicks: 1247, conversions: 312 },
      },
      {
        id: "asset-1",
        type: "asset",
        name: "Product Brochure",
        description: "PDF • 2.4 MB",
        color: "purple",
        stats: { views: 834, downloads: 267 },
      },
      {
        id: "auto-1",
        type: "autoResponder",
        name: "Welcome Message",
        description: "Hi! Thanks for your interest...",
        color: "green",
        stats: { sent: 1247, responses: 851 },
      },
      {
        id: "dest-1",
        type: "destination",
        name: "Product Demo Page",
        description: "demo.activqms.com",
        color: "orange",
        stats: { visits: 1089, conversions: 267 },
      },
    ],
    metrics: {
      totalInteractions: 2847,
      conversionRate: 28.4,
      responseRate: 68.2,
      avgEngagementTime: "2m 34s",
    },
  };

  const getComponentIcon = (type: string) => {
    const icons = {
      client: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
      link: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
          />
        </svg>
      ),
      asset: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
      autoResponder: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
      ),
      destination: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
          />
        </svg>
      ),
    };
    return icons[type as keyof typeof icons];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">
                {conversation.name}
              </h1>
              <span
                className={`px-3 py-1 text-sm font-medium rounded-full ${
                  conversation.status === "active"
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {conversation.status.charAt(0).toUpperCase() +
                  conversation.status.slice(1)}
              </span>
            </div>
            <p className="text-gray-600">{conversation.description}</p>
            <p className="text-sm text-gray-500 mt-1">
              Created on {new Date(conversation.createdAt).toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Link
              to={`/conversations/create?edit=${conversation.id}`}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Edit Conversation
            </Link>
            <Link
              to="/conversations"
              className="text-gray-500 hover:text-gray-700 font-medium"
            >
              ← Back to Conversations
            </Link>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Total Interactions
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {conversation.metrics.totalInteractions.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Conversion Rate
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {conversation.metrics.conversionRate}%
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Response Rate</p>
              <p className="text-2xl font-semibold text-gray-900">
                {conversation.metrics.responseRate}%
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Avg. Engagement
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {conversation.metrics.avgEngagementTime}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Conversation Components */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Conversation Components
          </h2>
          <p className="text-sm text-gray-600">
            Reusable components attached to this conversation
          </p>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {conversation.components.map((component) => (
              <div
                key={component.id}
                className={`border border-${component.color}-200 bg-${component.color}-50 rounded-lg p-4`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className={`text-${component.color}-600 mt-1`}>
                      {getComponentIcon(component.type)}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">
                        {component.name}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {component.description}
                      </p>
                      <p className="text-xs text-gray-400 capitalize mt-1">
                        {component.type}
                      </p>

                      {/* Component Stats */}
                      {component.stats && (
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-600">
                          {Object.entries(component.stats).map(
                            ([key, value]) => (
                              <span key={key}>
                                {key}:{" "}
                                <span className="font-medium">
                                  {value.toLocaleString()}
                                </span>
                              </span>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <button className="text-gray-400 hover:text-gray-600">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* User Journey Flow */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            User Journey Flow
          </h2>
          <p className="text-sm text-gray-600">
            How users interact with your conversation components
          </p>
        </div>
        <div className="p-6">
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="flex items-center space-x-4 overflow-x-auto">
              {conversation.components.map((component, index) => (
                <React.Fragment key={component.id}>
                  <div className="flex flex-col items-center min-w-0 flex-shrink-0">
                    <div
                      className={`w-12 h-12 bg-${component.color}-100 border-2 border-${component.color}-300 rounded-lg flex items-center justify-center text-${component.color}-600 mb-2`}
                    >
                      {getComponentIcon(component.type)}
                    </div>
                    <p className="text-xs font-medium text-gray-900 text-center">
                      {component.name}
                    </p>
                    <p className="text-xs text-gray-500 capitalize">
                      {component.type}
                    </p>
                  </div>
                  {index < conversation.components.length - 1 && (
                    <div className="flex-shrink-0">
                      <svg
                        className="w-6 h-6 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
